// SCREEN-HELD PR TEXT (H7 #5444, named end to end in #6339) — the one rule for WHEN
// a withheld author-text field holds the merge, and the mothership's read of the
// screen's verdict row.
//
// THE SCREEN. Before any review prompt quotes a pull request's third-party text, the
// untrusted-input screen judges it; a DISCARDED field is withheld from every prompt
// and the review posts `request_changes` over text no model read
// (`@developerz/review` `verdictWithDiscardedPrText`). What the screen row records is
// kinds and reasons, NEVER the text — the text is the thing the screen refused to
// show a model.
//
// WHY THIS LIVES IN DOMAIN. The rule below is written by the box
// (`apps/runner` `review/pipeline/pr-screen.ts`, which computes the stored
// `pr_text_holding` count with it) and read by the mothership (the merge egress's
// `screen_held_pr_text` refusal, the babysit hold's wait reason, the hold-status
// comment), and `@developerz/domain` imports nothing internal — so the shared half
// had to live here or be copied.

import { AUTHOR_TRUSTS, type BoxAuthorTrust } from './author-trust';
import { SCREEN_DISCARD_REASONS } from './intake-screen';

/**
 * The author-text fields a review screens, as the `review_pr_screen` row's
 * `withheld_kinds` names them. Closed and shared: the box writes these strings, the
 * mothership reads them back. The row may also name screen-item kinds that are NOT
 * PR text (`ci_check_name`, `prior_discussion_comment`); those are not in this set
 * and never hold the merge.
 */
export const WITHHELD_PR_FIELD_KINDS = [
  'pr_title',
  'pr_description',
  'issue_title',
  'issue_body',
] as const;

/** One of {@link WITHHELD_PR_FIELD_KINDS}. */
export type WithheldPrFieldKind = (typeof WITHHELD_PR_FIELD_KINDS)[number];

/** Who opened the pull request, as far as the merge hold reads it. */
export type WithheldFieldAuthor = {
  /** GitHub's author rung, off the PR object; `null` when it was not read (the lowest rung). */
  readonly trust: BoxAuthorTrust | null;
  /**
   * The PR was opened by OUR App — GitHub's author login, stamped by the worker as the
   * envelope's `review.app_authored` (`task-envelope/review-spec.ts`, `isSelfAuthored`),
   * never inferred from text. An UNSTAMPED envelope is `false` here: the side that holds.
   */
  readonly appAuthored: boolean;
};

/**
 * Whether a DISCARDED field holds the merge. Every one does, except the pull request's
 * own title and description when their author is a project member (`authorTrust`'s
 * `project` rung: OWNER, MEMBER or COLLABORATOR) or our own App (#7586 D6).
 *
 * The screen exists to keep a STRANGER's instructions away from a working agent. A
 * member's text is screened all the same — trust never makes an injection ok
 * (`prompts/screen.md`) — and a discarded field is withheld from every prompt whoever
 * wrote it. What the rung changes is only the consequence: a member can merge this pull
 * request themselves, so failing their review over text no model read protects nothing,
 * and it failed the repo owner's own release PR on its title. Our App's PR is the same
 * case one step further: its title and body are our composer's footer around our own
 * model's prose, and holding it parked the platform's own PRs on text it wrote.
 *
 * The task contract always holds: it is an issue's text, the PR author says nothing
 * about who wrote it, and an unread author (`null`, not app-authored) is the lowest rung.
 */
export function withheldFieldHolds(
  field: { readonly kind: WithheldPrFieldKind },
  author: WithheldFieldAuthor,
): boolean {
  const ownText = field.kind === 'pr_title' || field.kind === 'pr_description';
  return !(ownText && (author.appAuthored || author.trust === 'project'));
}

/** One PR-text field the screen held on a review run: which one and its reason, never the text. */
export type ScreenHeldPrField = {
  readonly kind: WithheldPrFieldKind;
  readonly reason: string;
};

/**
 * The reason words a real writer can stamp after the colon in a `withheld_kinds`
 * entry (`apps/runner` `pr-screen.ts`): the screen's closed discard reasons, or
 * `unavailable` when the screen never answered. The row is box-written and never
 * validated at the write, so the reason is checked against this set BEFORE it is
 * printed — the refusal detail, the hold status and the escalation all quote it, and
 * a box-invented string is attacker prose in a maintainer's inbox. Anything unknown
 * reads {@link SCREEN_HELD_REASON_UNKNOWN}, never echoed.
 */
export const SCREEN_HELD_PR_REASONS: readonly string[] = [...SCREEN_DISCARD_REASONS, 'unavailable'];

/** The one label an unrecognised screen reason is printed as — the word itself is dropped. */
export const SCREEN_HELD_REASON_UNKNOWN = 'unrecognised';

/** The stored reason word, printed only when a real writer could have stamped it. */
function screenHeldReason(raw: string): string {
  return SCREEN_HELD_PR_REASONS.includes(raw) ? raw : SCREEN_HELD_REASON_UNKNOWN;
}

/**
 * The field named to a human, one per kind. The box's own spelling
 * (`@developerz/review` `withheldPrText`) interpolates the issue number into the two
 * contract fields for its disclosure; the mothership's read of the row has no number
 * to interpolate, so these name the field alone.
 */
export const WITHHELD_PR_FIELD_LABEL: Record<WithheldPrFieldKind, string> = {
  pr_title: 'the pull request title',
  pr_description: 'the pull request description',
  issue_title: "the task contract's title",
  issue_body: "the task contract's body",
};

/**
 * The stored `review_pr_screen` row → the PR-text fields whose withholding HOLDS the
 * merge. The input is ONE object read off the same run's screen status row:
 * `withheld_kinds` (the box's `"<kind>:<reason>"` list of every non-ok screen
 * verdict, PR text and list items alike), `pr_author_trust` (the rung the box
 * stamped) and `pr_app_authored` (`true` when the box's envelope said our App opened
 * the PR; anything else reads `false`) — all untrusted text read back off
 * `task_run_events`.
 *
 * Answers `null` when there is no usable row to read (absent, not an object, no list
 * on it) — "no screen row to name" rather than "nothing held" — and never throws:
 * this read steers a merge refusal, so a malformed payload becomes a vaguer name,
 * never an exception. Entries naming a kind outside {@link WITHHELD_PR_FIELD_KINDS}
 * are NOT PR text and drop out; an entry in a shape this reader does not know drops
 * too rather than be guessed at — the hold itself never depends on this list (the
 * verdict already refused), only its NAME does.
 *
 * The hold decision is re-derived with {@link withheldFieldHolds} rather than read
 * off the stored `pr_text_holding` count: the box computed that count, and the box
 * is untrusted input on a read that steers a merge. The reason word goes through
 * {@link screenHeldReason} for the same cause: a writer stamps only
 * {@link SCREEN_HELD_PR_REASONS}, and anything else is printed `unrecognised`,
 * never echoed.
 */
export function parseScreenHeldPrText(row: unknown): readonly ScreenHeldPrField[] | null {
  if (row === null || typeof row !== 'object') return null;
  const fields = row as Record<string, unknown>;
  const kinds = fields['withheld_kinds'];
  if (!Array.isArray(kinds)) return null;
  const rawTrust = fields['pr_author_trust'];
  const rawTrustString = typeof rawTrust === 'string' ? rawTrust : null;
  // The raw stamp narrows to a rung by membership, so a future rung the table gains
  // reaches `withheldFieldHolds` instead of being silently read as the lowest one.
  const trust: BoxAuthorTrust | null =
    rawTrustString !== null &&
    (AUTHOR_TRUSTS as readonly string[]).includes(rawTrustString) &&
    rawTrustString !== 'platform'
      ? (rawTrustString as BoxAuthorTrust)
      : null;
  const author: WithheldFieldAuthor = { trust, appAuthored: fields['pr_app_authored'] === true };
  const held: ScreenHeldPrField[] = [];
  for (const entry of kinds) {
    if (typeof entry !== 'string') continue;
    const colon = entry.indexOf(':');
    const kind = colon === -1 ? entry : entry.slice(0, colon);
    if (!(WITHHELD_PR_FIELD_KINDS as readonly string[]).includes(kind)) continue;
    const field = { kind: kind as WithheldPrFieldKind };
    if (!withheldFieldHolds(field, author)) continue;
    held.push({
      kind: field.kind,
      reason: colon === -1 ? 'withheld' : screenHeldReason(entry.slice(colon + 1)),
    });
  }
  return held;
}
